<template>
  <div class="staff-employee-detail">
    <LoadSpinner v-if="loading" style="padding: 2rem 0" />

    <div v-if="employee" class="container">
      <div class="card employee-card">
        <EmployeeCard :employee="employee" />
        <div class="card__section card__section--bordered">
          <h5 class="text-blue text-upper text-small">
            {{ employee.firstName.trim() || 'Unnamed User' }}'s HOUSEHOLD
          </h5>
          <LoadSpinner v-if="householdDetails.loading" />
          <div v-else>
            <p class="text-small">
              <strong>Adults in household: {{ householdDetails.adultsInHousehold }}</strong>
            </p>
            <p class="text-small">
              <strong>Children in household: {{ householdDetails.childrenInHousehold }}</strong>
            </p>
          </div>
        </div>
      </div>
      <div class="content-right">
        <div class="card fit-content">
          <div class="requests-container card__section">
            <h2>Request History</h2>
            <LoadSpinner v-if="requests.refreshing" />
            <div v-else>
              <md-table v-if="requests.list.length">
                <md-table-row class="header-row">
                  <md-table-head> Date Created </md-table-head>
                  <md-table-head>Date Submitted</md-table-head>
                  <md-table-head> Amount </md-table-head>
                  <md-table-head> Request Status </md-table-head>
                  <md-table-head>Disbursed Amount</md-table-head>
                </md-table-row>
                <md-table-row
                  class="body-row"
                  v-for="request in requests.list"
                  :key="request.id"
                  @click.native="() => onRequestRowClick(request)"
                >
                  <md-table-cell>{{ request.datetimeCreated | formatDateShort }}</md-table-cell>
                  <md-table-cell>{{ request.submittedOn | formatDateShort }}</md-table-cell>
                  <md-table-cell>{{ request.amount | currency }}</md-table-cell>
                  <md-table-cell>{{ request.statusDisplay }}</md-table-cell>
                  <md-table-cell>{{ request.approvedFunds | currency }}</md-table-cell>
                </md-table-row>
              </md-table>
              <p v-else>This employee does not have a request history.</p>
            </div>
          </div>
          <div class="payouts-container card__section card__section--bordered">
            <h2>Payout History</h2>
            <LoadSpinner v-if="payouts.refreshing" />
            <div v-else>
              <md-table v-if="payouts.list.length">
                <md-table-row class="header-row">
                  <md-table-head> Date Created </md-table-head>
                  <md-table-head> Amount </md-table-head>
                  <md-table-head> Status </md-table-head>
                </md-table-row>
                <md-table-row class="body-row" v-for="payout in payouts.list" :key="payout.id">
                  <md-table-cell>{{ payout.datetimeCreated | formatDateShort }}</md-table-cell>
                  <md-table-cell>{{ payout.amount | currency }}</md-table-cell>
                  <md-table-cell>{{ payout.status }}</md-table-cell>
                </md-table-row>
              </md-table>
              <p v-else>This employee does not have a payout history.</p>
            </div>
          </div>
        </div>

        <div class="card financial-health fit-content">
          <div class="card__section">
            <h2>Financial Health</h2>
            <LoadSpinner v-if="financialHealth.loading" />
            <div v-else>
              <div v-if="financialHealth.surveyResponse">
                <div
                  class="question"
                  v-for="question in financialHealth.surveyResponse.questions"
                  :key="question.id"
                >
                  <span>{{ question.text }}</span>
                  <p>{{ question.answer.textEn || question.answer.text }}</p>
                </div>
              </div>
              <p v-else>This employee has not completed the survey.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import LoadSpinner from '@/components/LoadSpinner'

import EmployeeCard from '@/views/common/EmployeeCard'

import Employee from '@/services/employees'
import Request from '@/services/requests'
import Payout from '@/services/payouts'
import SurveyResponse from '@/services/surveyresponses'
import CollectionManager from '@/services/collectionManager'

export default {
  name: 'EmployeeDetail',
  components: {
    LoadSpinner,
    EmployeeCard,
  },
  data() {
    return {
      loading: false,
      employee: null,
      // TODO (Bruno 4-30-20): Include pagination UX, since requests and payouts are paginated to first 25
      requests: CollectionManager.create({
        ModelClass: Request,
        filters: {
          employee: this.$route.params.id,
        },
      }),
      payouts: CollectionManager.create({
        ModelClass: Payout,
        filters: {
          employee: this.$route.params.id,
        },
      }),
      householdDetails: {
        loading: false,
        adultsInHousehold: null,
        childrenInHousehold: null,
      },
      financialHealth: {
        loading: false,
        surveyResponse: null,
      },
    }
  },
  created() {
    this.refresh()
    this.getHouseholdDetails()
    this.getFinancialHealthSurveyResponse()
  },
  methods: {
    getAnswerFromOption(question) {
      const option = question.answerOptions.find((option) => {
        return option.id === question.answer.option
      })

      return option.textEn
    },
    refresh() {
      this.loading = true
      Employee.api
        .retrieve(this.$route.params.id)
        .then((employee) => {
          this.employee = employee
        })
        .finally(() => {
          this.loading = false
        })
      this.requests.refresh()
      this.payouts.refresh()
    },
    onRequestRowClick(request) {
      const routeData = this.$router.resolve({
        name: 'StaffRequestDetail',
        params: {
          id: request.id,
        },
      })

      window.open(routeData.href, '_blank');
    },
    /**
     * @function getHouseholdDetails
     * The goal of this function is to produce this employee's most recent household details ( # adults, # children).
     * In order to do this, it:
     *    1 -- fetches an ordered set of this employee's SurveyResponses for the 'Household Details' survey,
     *    2 -- checks if this employee has ever completed the 'Household Details' survey:
     *          i -- if they have then it finds the most recent
     *          ii - if they have not then it updates component state to 'N/A'
     *    3 -- once the most recent completedSurvey is found, it fetches the completedSurvey's full details, to grab answers,
     *    4 -- updates component state accordingly
     */
    getHouseholdDetails() {
      this.householdDetails.loading = true

      CollectionManager.create({
        ModelClass: SurveyResponse,
        filters: {
          employee: this.$route.params.id,
          surveyName: 'Household Details',
          ordering: '-datetime_created',
        },
      })
        .refresh()
        .then(this.grabMostRecentCompleteResponse)
        .then((completedSurveyResponse) => {
          // if a completedSurvey was returned from the previous Promise, retrieve its details and return that Promise
          // else all of the survey-responses were incomplete, so throw a catchable error
          if (completedSurveyResponse) {
            return SurveyResponse.api.retrieve(completedSurveyResponse.id)
          } else {
            throw Error('Household Details not available.')
          }
        })
        .then((retrievedSurveyResponse) => {
          // update component state with retrievedSurveyResponse's answers for appropriate questions
          let activeQuestions = retrievedSurveyResponse.questions.filter((q) => !q.isArchived)
          const adultsInHouseholdQuestion = activeQuestions.find(question => question['humanReadableId'] == 5)
          const childrenInHouseholdQuestion = activeQuestions.find(question => question['humanReadableId'] == 6)
          this.householdDetails = {
            loading: false,
            adultsInHousehold: adultsInHouseholdQuestion && adultsInHouseholdQuestion.answer.text,
            childrenInHousehold: childrenInHouseholdQuestion && childrenInHouseholdQuestion.answer.text,
          }
        })
        .catch(() => {
          // update component state to reflect the fact that there is no data for a 'Household Details' completed survey-response for this employee
          this.householdDetails = {
            loading: false,
            adultsInHousehold: 'N/A',
            childrenInHousehold: 'N/A',
          }
        })
    },
    /**
     * @function getFinancialHealthSurveyResponse
     * Similar to @function getHouseholdDetails,
     * The goal of this function is to produce this employee's most recent financial health survey response.
     * In order to do this, it:
     *    1 -- fetches an ordered set of this employee's SurveyResponses for the 'Financial Health' survey,
     *    2 -- checks if this employee has ever completed the 'Financial Health' survey:
     *          i -- if they have then it finds the most recent
     *          ii - if they have not then it updates component state to 'N/A'
     *    3 -- once the most recent completedSurvey is found, it fetches the completedSurvey's full details, to grab answers,
     *    4 -- updates component state accordingly
     */
    getFinancialHealthSurveyResponse() {
      this.financialHealth.loading = true

      CollectionManager.create({
        ModelClass: SurveyResponse,
        filters: {
          employee: this.$route.params.id,
          surveyName: 'Financial Health',
          ordering: '-datetime_created',
        },
      })
        .refresh()
        .then(this.grabMostRecentCompleteResponse)
        .then((completedSurveyResponse) => {
          // if a completedSurvey was returned from the previous Promise, retrieve its details and return that Promise
          // else all of the survey-responses were incomplete, so throw a catchable error
          if (completedSurveyResponse) {
            return SurveyResponse.api.retrieve(completedSurveyResponse.id)
          } else {
            throw Error('Financial Health answers not available.')
          }
        })
        .then((retrievedSurveyResponse) => {
          // update component state with retrievedSurveyResponse
          this.financialHealth = {
            loading: false,
            surveyResponse: retrievedSurveyResponse,
          }
        })
        .catch(() => {
          // update component state to reflect the fact that there is no data for a 'Financial Health' completed survey-response for this employee
          this.financialHealth = {
            loading: false,
            surveyResponse: false,
          }
        })
    },
    grabMostRecentCompleteResponse({ list }) {
      // if the list is populated, find the most recent survey-response that is completed
      // else the list is empty, so return null to be processed in the following .then()
      if (list.length) {
        let completedSurveyResponse = null
        let currentIndex = 0
        let maxIndex = list.length - 1

        while (!completedSurveyResponse || currentIndex <= maxIndex) {
          let currentSurveyResponse = list[currentIndex]
          ++currentIndex
          if (currentSurveyResponse.isComplete) {
            completedSurveyResponse = currentSurveyResponse
          }
        }

        return completedSurveyResponse
      } else {
        return null
      }
    },
  },
}
</script>

<style lang="scss" scoped>
@import '../../styles/mixins/containers';
@import '../../styles/mixins/cards';

.staff-employee-detail {
  @include screen-container;
}

.container {
  @include flex-container;
  flex-flow: row;
}

.card {
  @include card;
}

.employee-card {
  align-self: flex-start;
  flex-grow: 1;
  margin-right: 1rem;

  @media (min-width: $mobile-break-point) {
    min-width: 24rem;
  }
}

.content-right {
  width: 52rem;
  display: flex;
  flex-flow: column;
}

.table-header {
  display: flex;

  &__start {
    display: flex;
    padding: 1rem 0 0 0;
  }

  &__end {
    flex: 4;
    align-self: flex-end;
    display: flex;
    align-items: center;
    justify-content: flex-end;
  }
}

.header-row {
  background-color: $soft-gray;

  & > .md-table-head {
    font-size: 1rem;
  }
}

.body-row {
  & > .md-table-cell {
    border-top: 1px solid $soft-gray;
    padding: 0.5rem;
  }

  &:hover {
    background-color: $off-white;
    cursor: pointer;
  }
}

.fit-content {
  height: fit-content;
}

.financial-health {
  margin-top: 2rem;

  .question {
    margin: 1rem 0rem;

    p {
      margin-top: 0.3rem;
    }
  }
}
</style>
